import { HubConnection, HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import { ConsoleFacade } from '../helpers/console-facade';
import { Emit, Vue } from 'vue-property-decorator';
import { IHubMessage } from '../api-client/models/activity-event/hub-message';
import { ArgumentValidator } from '../helpers';

export class WebSocketsHubCreator<T> extends Vue {

    private connection: HubConnection | undefined;
    private initialStartFailoverRetryInMs: number = 5000;

    @Emit('messageReceived')
    // tslint:disable-next-line:no-empty
    private emitMessageReceived(message: IHubMessage<T>): IHubMessage<T> {

        ArgumentValidator.notNullOrEmpty(message, `message`);

        return message;
    }

    @Emit('connected')
    // tslint:disable-next-line:no-empty
    private emitConnected(): void {
        return;
    }

    @Emit('disconnected')
    // tslint:disable-next-line:no-empty
    private emitDisconnected(): void {
        return;
    }

    @Emit('reconnected')
    // tslint:disable-next-line:no-empty
    private emitReconnected(): void {
        return;
    }

    public connect(url: string, userAccessToken: string, initialStartFailoverRetryInMs?: number, automaticReconnection = true): void {

        if (initialStartFailoverRetryInMs) {
            this.initialStartFailoverRetryInMs = initialStartFailoverRetryInMs;
        }

        const builder = new HubConnectionBuilder()
            .withUrl(url, {
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
                accessTokenFactory: () => encodeURI(userAccessToken) ?? ``
            });

        if (automaticReconnection) {
            builder.withAutomaticReconnect();
        }

        this.connection = builder.build();

        this.start();

        this.connection.onreconnecting(error => {

            ConsoleFacade.log(`WebSocketsHubCreator: Trying to reconnect to Hub`, error)
        });

        this.connection.onreconnected((connectionId?: string) => {

            ConsoleFacade.log(`WebSocketsHubCreator: Reconnected to Hub`, connectionId);

            this.emitReconnected();
        });

        this.connection.on('Event', (message: IHubMessage<T>) => {

            ConsoleFacade.log('WebSocketsHubCreator: Message received', message);

            this.emitMessageReceived(message);
        });

        this.connection.onclose(async () => {

            ConsoleFacade.log(`WebSocketsHubCreator: Hub connection closed.`)

            this.emitDisconnected();
        });
    }

    private async start(): Promise<void> {

        try {

            if (!this.connection) {

                ConsoleFacade.log(`WebSocketsHubCreator: Cancelled due to no connection defined.`)
                return;
            }

            await this.connection.start();

            ConsoleFacade.log(`WebSocketsHubCreator: Hub connection started.`)

            this.emitConnected();

        } catch (error) {

            ConsoleFacade.error(`WebSocketsHubCreator: Hub connection error while starting connection.`, error)

            if (this.initialStartFailoverRetryInMs > 0) {
                ConsoleFacade.error(`WebSocketsHubCreator: Will retry in ${this.initialStartFailoverRetryInMs} ms`, error)
                setTimeout(this.start, this.initialStartFailoverRetryInMs);
            }
        }
    }
}